import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Link } from '@tiptap/extension-link';
import { Image } from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Underline } from '@tiptap/extension-underline';
import { Placeholder } from '@tiptap/extension-placeholder';
import { TextAlign } from '@tiptap/extension-text-align';
import { Superscript } from '@tiptap/extension-superscript';
import {
  Bold, Italic, Underline as UnderlineIcon, Link as LinkIcon, Image as ImageIcon,
  Heading1, Heading2, Heading3, List, ListOrdered, Quote, Table as TableIcon,
  AlignLeft, AlignCenter, AlignRight, Undo, Redo, Code, Minus, FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const ToolbarButton = ({ onClick, isActive, icon: Icon, title, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`p-2 rounded transition-colors ${isActive ? 'bg-[#36c] text-white' : 'text-[#54595d] hover:text-[#202122] hover:bg-[#eaecf0]'} ${disabled ? 'opacity-30 cursor-not-allowed' : ''}`}
    title={title}
    data-testid={`editor-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <Icon className="h-4 w-4" />
  </button>
);

const ToolbarDivider = () => <div className="w-px h-6 bg-[#c8ccd1] mx-1" />;

export const TipTapEditor = ({ content, onChange, placeholder = "Start writing..." }) => {
  const [linkUrl, setLinkUrl] = React.useState('');
  const [imageUrl, setImageUrl] = React.useState('');
  const [linkDialogOpen, setLinkDialogOpen] = React.useState(false);
  const [imageDialogOpen, setImageDialogOpen] = React.useState(false);
  const [citationDialogOpen, setCitationDialogOpen] = React.useState(false);
  const [citationText, setCitationText] = React.useState('');
  const [citationUrl, setCitationUrl] = React.useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-[#36c] underline cursor-pointer',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full rounded my-4 border border-[#c8ccd1]',
        },
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Underline,
      Superscript,
      Placeholder.configure({
        placeholder,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  const addLink = () => {
    if (linkUrl) {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: linkUrl })
        .run();
      setLinkUrl('');
      setLinkDialogOpen(false);
    }
  };

  const addImage = () => {
    if (imageUrl) {
      editor.chain().focus().setImage({ src: imageUrl }).run();
      setImageUrl('');
      setImageDialogOpen(false);
    }
  };

  const addCitation = () => {
    if (citationText) {
      const citationNumber = document.querySelectorAll('.citation-ref').length + 1;
      let citationHtml = '';
      
      if (citationUrl) {
        citationHtml = `<sup class="citation-ref"><a href="${citationUrl}" target="_blank" rel="noopener noreferrer" class="text-[#36c] hover:underline">[${citationNumber}]</a></sup>`;
      } else {
        citationHtml = `<sup class="citation-ref text-[#36c]">[${citationNumber}]</sup>`;
      }
      
      editor.chain().focus().insertContent(citationHtml).run();
      
      const currentContent = editor.getHTML();
      if (!currentContent.includes('id="references"')) {
        editor.chain().focus().insertContent(`<hr><div id="references"><h3>References</h3><ol><li>${citationUrl ? `<a href="${citationUrl}" target="_blank" rel="noopener noreferrer" class="text-[#36c]">${citationText}</a>` : citationText}</li></ol></div>`).run();
      } else {
        const refSection = currentContent.match(/<ol>(.*?)<\/ol>/s);
        if (refSection) {
          const newRef = `${refSection[1]}<li>${citationUrl ? `<a href="${citationUrl}" target="_blank" rel="noopener noreferrer" class="text-[#36c]">${citationText}</a>` : citationText}</li>`;
          const newContent = currentContent.replace(/<ol>(.*?)<\/ol>/s, `<ol>${newRef}</ol>`);
          editor.commands.setContent(newContent);
        }
      }
      
      setCitationText('');
      setCitationUrl('');
      setCitationDialogOpen(false);
    }
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  if (!editor) {
    return <div className="animate-pulse bg-[#f8f9fa] rounded h-96" />;
  }

  return (
    <div className="tiptap-editor bg-white" data-testid="tiptap-editor">
      {/* Toolbar */}
      <div className="editor-toolbar flex flex-wrap gap-1 p-3 border-b border-[#c8ccd1] bg-[#f8f9fa]">
        {/* Undo/Redo */}
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          icon={Undo}
          title="Undo"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          icon={Redo}
          title="Redo"
        />

        <ToolbarDivider />

        {/* Headings */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive('heading', { level: 1 })}
          icon={Heading1}
          title="Heading 1"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          icon={Heading2}
          title="Heading 2"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
          icon={Heading3}
          title="Heading 3"
        />

        <ToolbarDivider />

        {/* Text Formatting */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          icon={Bold}
          title="Bold"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          icon={Italic}
          title="Italic"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          icon={UnderlineIcon}
          title="Underline"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCode().run()}
          isActive={editor.isActive('code')}
          icon={Code}
          title="Code"
        />

        <ToolbarDivider />

        {/* Alignment */}
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          isActive={editor.isActive({ textAlign: 'left' })}
          icon={AlignLeft}
          title="Align Left"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          isActive={editor.isActive({ textAlign: 'center' })}
          icon={AlignCenter}
          title="Align Center"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          isActive={editor.isActive({ textAlign: 'right' })}
          icon={AlignRight}
          title="Align Right"
        />

        <ToolbarDivider />

        {/* Lists */}
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          icon={List}
          title="Bullet List"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          icon={ListOrdered}
          title="Numbered List"
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          icon={Quote}
          title="Quote"
        />

        <ToolbarDivider />

        {/* Link */}
        <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
          <DialogTrigger asChild>
            <button
              type="button"
              className={`p-2 rounded transition-colors ${editor.isActive('link') ? 'bg-[#36c] text-white' : 'text-[#54595d] hover:text-[#202122] hover:bg-[#eaecf0]'}`}
              title="Add Link"
              data-testid="editor-add-link"
            >
              <LinkIcon className="h-4 w-4" />
            </button>
          </DialogTrigger>
          <DialogContent className="bg-white border-[#a2a9b1]">
            <DialogHeader>
              <DialogTitle className="text-[#202122]">Add Link</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="link-url" className="text-[#202122]">URL</Label>
                <Input
                  id="link-url"
                  placeholder="https://example.com"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  className="bg-white border-[#a2a9b1] text-[#202122] placeholder:text-[#72777d]"
                  data-testid="link-url-input"
                />
              </div>
              <Button onClick={addLink} className="w-full bg-[#36c] hover:bg-[#2a4b8d] text-white" data-testid="add-link-btn">
                Add Link
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Image */}
        <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
          <DialogTrigger asChild>
            <button
              type="button"
              className="p-2 rounded transition-colors text-[#54595d] hover:text-[#202122] hover:bg-[#eaecf0]"
              title="Add Image"
              data-testid="editor-add-image"
            >
              <ImageIcon className="h-4 w-4" />
            </button>
          </DialogTrigger>
          <DialogContent className="bg-white border-[#a2a9b1]">
            <DialogHeader>
              <DialogTitle className="text-[#202122]">Add Image</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="image-url" className="text-[#202122]">Image URL</Label>
                <Input
                  id="image-url"
                  placeholder="https://example.com/image.jpg"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  className="bg-white border-[#a2a9b1] text-[#202122] placeholder:text-[#72777d]"
                  data-testid="image-url-input"
                />
              </div>
              <Button onClick={addImage} className="w-full bg-[#36c] hover:bg-[#2a4b8d] text-white" data-testid="add-image-btn">
                Add Image
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Citation */}
        <Dialog open={citationDialogOpen} onOpenChange={setCitationDialogOpen}>
          <DialogTrigger asChild>
            <button
              type="button"
              className="p-2 rounded transition-colors text-[#54595d] hover:text-[#202122] hover:bg-[#eaecf0]"
              title="Add Citation"
              data-testid="editor-add-citation"
            >
              <FileText className="h-4 w-4" />
            </button>
          </DialogTrigger>
          <DialogContent className="bg-white border-[#a2a9b1]">
            <DialogHeader>
              <DialogTitle className="text-[#202122]">Add Citation</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="citation-text" className="text-[#202122]">Citation Text</Label>
                <Textarea
                  id="citation-text"
                  placeholder="Author, Title, Publication, Year..."
                  value={citationText}
                  onChange={(e) => setCitationText(e.target.value)}
                  className="bg-white border-[#a2a9b1] text-[#202122] placeholder:text-[#72777d]"
                  rows={3}
                  data-testid="citation-text-input"
                />
                <p className="text-xs text-[#54595d]">Enter the full citation information</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="citation-url" className="text-[#202122]">Source URL (optional)</Label>
                <Input
                  id="citation-url"
                  placeholder="https://source.com/article"
                  value={citationUrl}
                  onChange={(e) => setCitationUrl(e.target.value)}
                  className="bg-white border-[#a2a9b1] text-[#202122] placeholder:text-[#72777d]"
                  data-testid="citation-url-input"
                />
              </div>
              <Button onClick={addCitation} className="w-full bg-[#36c] hover:bg-[#2a4b8d] text-white" data-testid="add-citation-btn">
                Add Citation
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Table */}
        <ToolbarButton
          onClick={insertTable}
          icon={TableIcon}
          title="Insert Table"
        />

        {/* Horizontal Rule */}
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          icon={Minus}
          title="Horizontal Rule"
        />
      </div>

      {/* Editor Content */}
      <EditorContent 
        editor={editor} 
        className="prose prose-lg max-w-none min-h-[400px] focus:outline-none [&_.ProseMirror]:min-h-[400px] [&_.ProseMirror]:p-6 [&_.ProseMirror]:outline-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-[#72777d] [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0" 
      />

      <style>{`
        .tiptap-editor .ProseMirror {
          color: #202122;
          background: #ffffff;
          min-height: 400px;
          padding: 1.5rem;
        }
        .tiptap-editor .ProseMirror h1,
        .tiptap-editor .ProseMirror h2,
        .tiptap-editor .ProseMirror h3 {
          color: #202122;
          font-family: 'Linux Libertine', 'Georgia', serif;
        }
        .tiptap-editor .ProseMirror h1,
        .tiptap-editor .ProseMirror h2 {
          border-bottom: 1px solid #a2a9b1;
          padding-bottom: 0.25rem;
        }
        .tiptap-editor .ProseMirror a {
          color: #36c;
        }
        .tiptap-editor .ProseMirror blockquote {
          border-left-color: #c8ccd1;
          color: #54595d;
        }
        .tiptap-editor .ProseMirror code {
          background: #f8f9fa;
          color: #202122;
          border: 1px solid #c8ccd1;
          border-radius: 2px;
          padding: 0.1rem 0.3rem;
        }
        .tiptap-editor .ProseMirror pre {
          background: #f8f9fa;
          border: 1px solid #c8ccd1;
        }
        .tiptap-editor .ProseMirror hr {
          border-color: #c8ccd1;
        }
        .tiptap-editor .ProseMirror table {
          border-color: #a2a9b1;
        }
        .tiptap-editor .ProseMirror th,
        .tiptap-editor .ProseMirror td {
          border-color: #a2a9b1;
        }
        .tiptap-editor .ProseMirror th {
          background: #eaecf0;
        }
      `}</style>
    </div>
  );
};

export default TipTapEditor;
